import { useEffect, useMemo, useState } from "react";
import { useTranslate } from "ra-core";
import { apiJson } from "@/lib/apiClient";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { InputBase, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { TextArea } from "@/components/base/textarea/textarea";
import { Dialog } from "@/components/application/modals/modal";
import {
  AppModal,
  AppModalOverlay,
} from "@/components/atomic-crm/kit/app-modal";
import type { Lead, ProjectInterest } from "../types";
import {
  candidateProfileDraft,
  candidateProfileFields,
  changedCandidateProfileValues,
  type CandidateProfileDraft,
  type CandidateProfileUpdate,
} from "../leads/domain/candidateProfile";
import { formatCandidateNotes } from "./domain/candidate-notes";
import {
  Building2,
  BusFront,
  CalendarDays,
  Check,
  CircleDollarSign,
  FileBadge,
  Handshake,
  Home,
  LoaderCircle,
  MapPin,
  NotepadText,
  Pencil,
  Phone,
  Save,
  UserRound,
  X,
  type LucideIcon,
} from "lucide-react";

const display = (value: unknown, fallback: string) => {
  if (value === undefined || value === null) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

type CandidateInfoItem = {
  key: string;
  label: string;
  value: string;
  noteItems?: string[];
  complete: boolean;
  Icon: LucideIcon;
};

const hasMeaningfulValue = (value: unknown) => display(value, "") !== "";

// ``projects`` (Dự án quan tâm) renders only when the candidate has engaged a
// project, so listing it here adds a row to see, never an empty one.
const PRIORITY_INFO_KEYS = [
  "phone",
  "name",
  "expectation",
  "birth",
  "projects",
];
const PRIORITY_EDIT_KEYS = ["phone", "name", "desired_job", "birth_year"];
const orderedProfileFields = [...candidateProfileFields].sort(
  (first, second) => {
    const rank = (key: string) => {
      const index = PRIORITY_EDIT_KEYS.indexOf(key);
      return index === -1 ? PRIORITY_EDIT_KEYS.length : index;
    };
    return rank(first.key) - rank(second.key);
  },
);

const notesInclude = (notes: string | null | undefined, terms: string[]) => {
  const normalized = notes?.toLocaleLowerCase("vi-VN") ?? "";
  return terms.some((term) => normalized.includes(term));
};

export const ConversationContextPanel = ({
  lead,
  open,
  persistent = false,
  canEdit = false,
  onSave,
  onClose,
  onCloseAutoFocus,
}: {
  lead?: Lead;
  open: boolean;
  persistent?: boolean;
  canEdit?: boolean;
  onSave?: (
    changes: Partial<CandidateProfileUpdate>,
    version: number,
  ) => Promise<void>;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}) => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const [savingLeadId, setSavingLeadId] = useState<string | null>(null);
  const leadId = String(lead?.id ?? "empty");
  const isSaving = savingLeadId === leadId;
  // Which dự án this candidate has engaged with (bot project focus, captured
  // on either channel). Read per lead; a failed or empty read simply omits the
  // row — interest is a hint, never an error surface.
  const [projectInterests, setProjectInterests] = useState<ProjectInterest[]>(
    [],
  );
  const interestLeadId = lead?.id ?? null;
  useEffect(() => {
    if (interestLeadId === null) {
      setProjectInterests([]);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    apiJson<ProjectInterest[]>(
      `/api/v1/leads/${interestLeadId}/project-interests`,
      { signal: controller.signal },
    )
      .then((rows) => {
        if (!cancelled) setProjectInterests(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setProjectInterests([]);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [interestLeadId]);
  const candidateInfoItems = useMemo<CandidateInfoItem[]>(() => {
    const noData = translate("crm.common.no_data");
    const notes = lead?.notes;
    const noteItems = formatCandidateNotes(notes);
    const dateOfBirth = lead?.birth_year
      ? String(lead.birth_year)
      : lead?.age
        ? `${lead.age} tuổi`
        : "";
    const area = [lead?.region, lead?.living_area].filter(Boolean).join(" · ");
    const hasCitizenId = notesInclude(notes, ["cccd", "cmnd", "căn cước"]);
    const hasHousing = notesInclude(notes, [
      "chỗ ở",
      "cho o",
      "nhà trọ",
      "nha tro",
      "ký túc",
      "ky tuc",
      "ktx",
    ]);
    const hasPickup = notesInclude(notes, [
      "đưa đón",
      "dua don",
      "xe đưa",
      "xe dua",
      "xe đón",
      "xe don",
      "bus",
      "tuyến xe",
      "tuyen xe",
    ]);

    const interestedProjects = projectInterests
      .map((row) => (row.project_name || row.project_slug || "").trim())
      .filter(Boolean);

    return [
      {
        key: "name",
        label: translate("leads.fields.name"),
        value: display(lead?.name, noData),
        complete: hasMeaningfulValue(lead?.name),
        Icon: UserRound,
      },
      {
        key: "phone",
        label: translate("leads.fields.phone"),
        value: display(lead?.phone, noData),
        complete: hasMeaningfulValue(lead?.phone),
        Icon: Phone,
      },
      {
        key: "birth",
        label: translate(
          lead?.birth_year || !lead?.age
            ? "leads.fields.birth_year"
            : "leads.fields.age",
        ),
        value: display(dateOfBirth, noData),
        complete: Boolean(dateOfBirth),
        Icon: CalendarDays,
      },
      {
        key: "citizen-id",
        label: translate("leads.fields.citizen_id"),
        value: hasCitizenId
          ? translate("leads.recorded_in_notes")
          : translate("leads.needs_follow_up"),
        complete: hasCitizenId,
        Icon: FileBadge,
      },
      {
        key: "experience",
        label: translate("leads.fields.experience"),
        value: display(lead?.years_experience, noData),
        complete: hasMeaningfulValue(lead?.years_experience),
        Icon: FileBadge,
      },
      {
        key: "expectation",
        label: "Nguyện vọng",
        value: display(lead?.desired_job, noData),
        complete: hasMeaningfulValue(lead?.desired_job),
        Icon: Handshake,
      },
      // Only when the candidate has actually engaged a project — a row of
      // "no data" for every lead would bury the signal it exists to show.
      ...(interestedProjects.length > 0
        ? [
            {
              key: "projects",
              label: "Dự án quan tâm",
              value: interestedProjects.join(" · "),
              complete: true,
              Icon: Building2,
            },
          ]
        : []),
      {
        key: "salary",
        label: translate("leads.fields.salary"),
        value: display(lead?.expected_salary, noData),
        complete: hasMeaningfulValue(lead?.expected_salary),
        Icon: CircleDollarSign,
      },
      {
        key: "housing",
        label: translate("leads.fields.housing"),
        value: hasHousing
          ? translate("leads.recorded_in_notes")
          : translate("leads.needs_follow_up"),
        complete: hasHousing,
        Icon: Home,
      },
      {
        key: "pickup",
        label: translate("leads.fields.pickup"),
        value: hasPickup
          ? translate("leads.recorded_in_notes")
          : translate("leads.needs_follow_up"),
        complete: hasPickup,
        Icon: BusFront,
      },
      {
        key: "area",
        label: translate("leads.fields.area"),
        value: display(area, noData),
        complete: Boolean(area),
        Icon: MapPin,
      },
      {
        key: "address",
        label: translate("leads.fields.address"),
        value: display(lead?.address, noData),
        complete: hasMeaningfulValue(lead?.address),
        Icon: Home,
      },
      {
        key: "notes",
        label: translate("leads.fields.notes"),
        value:
          noteItems.length > 0 ? noteItems.join("\n") : display(null, noData),
        noteItems: noteItems.length > 0 ? noteItems : undefined,
        complete: noteItems.length > 0,
        Icon: NotepadText,
      },
    ];
  }, [lead, translate, projectInterests]);
  const content = (
    <CandidateContextBody
      key={String(lead?.id ?? "empty")}
      lead={lead}
      candidateInfoItems={candidateInfoItems}
      canEdit={canEdit}
      isSaving={isSaving}
      onSavingChange={(saving) =>
        setSavingLeadId((current) =>
          saving ? leadId : current === leadId ? null : current,
        )
      }
      onSave={onSave}
      closeButtonPress={
        isMobile
          ? undefined
          : () => {
              if (!isSaving) onClose();
            }
      }
      showClose={!persistent}
    />
  );

  // Every non-wide-desktop band opens the context as the console's one modal
  // system (kit AppModal): a drag-dismissable bottom sheet under 640px, the
  // vendored centered modal at sm+. This replaces both legacy scrims — the
  // old slideout overlay and the fixed-panel `.context-overlay-scrim` — with
  // the vendored `bg-overlay/70` scrim.
  if (!persistent) {
    return (
      <AppModalOverlay
        isOpen={open}
        onOpenChange={(nextOpen) => {
          if (nextOpen || isSaving) return;
          // Preserve the caller's focus contract: the console wants focus back
          // on the trigger that opened the panel, which the dialog cannot know.
          onCloseAutoFocus?.(new Event("modal-close-autofocus"));
          onClose();
        }}
        isDismissable={!isSaving}
        isKeyboardDismissDisabled={isSaving}
        className="uu-scope z-50"
      >
        <AppModal className="candidate-context-sheet w-full outline-hidden sm:max-w-md">
          <Dialog
            id="conversation-context-panel"
            aria-label={translate("leads.profile_title")}
            className="flex flex-col gap-0 p-0 outline-hidden"
          >
            <div className="inbox-bg-container conversation-context-sheet-body">
              {content}
            </div>
          </Dialog>
        </AppModal>
      </AppModalOverlay>
    );
  }

  return (
    <aside
      id="conversation-context-panel"
      className={`panel right-panel ${open ? "context-open" : ""} ${persistent ? "context-persistent" : ""}`}
      aria-label={translate("leads.profile_title")}
      aria-hidden={!open}
    >
      {content}
    </aside>
  );
};

const CandidateContextBody = ({
  lead,
  candidateInfoItems,
  canEdit,
  isSaving,
  onSavingChange,
  onSave,
  closeButtonPress,
  showClose,
}: {
  lead?: Lead;
  candidateInfoItems: CandidateInfoItem[];
  canEdit: boolean;
  isSaving: boolean;
  onSavingChange: (saving: boolean) => void;
  onSave?: (
    changes: Partial<CandidateProfileUpdate>,
    version: number,
  ) => Promise<void>;
  /**
   * Called by the visible close button. Mobile renders the body inside a React
   * Aria dialog, where the button's `slot="close"` already closes the modal and
   * the overlay's `onOpenChange` notifies the owner — passing a handler there
   * would notify twice.
   */
  closeButtonPress?: () => void;
  showClose: boolean;
}) => {
  const [editSession, setEditSession] = useState<{
    initial: CandidateProfileDraft;
    draft: CandidateProfileDraft;
    version: number;
  } | null>(null);
  const [saveError, setSaveError] = useState<string>();
  const translate = useTranslate();

  const cancelEditing = () => {
    if (isSaving) return;
    setEditSession(null);
    setSaveError(undefined);
  };

  const saveProfile = async () => {
    if (!editSession || !onSave || isSaving) return;
    const changes = changedCandidateProfileValues(
      editSession.initial,
      editSession.draft,
    );
    if (Object.keys(changes).length === 0) {
      setEditSession(null);
      return;
    }
    setSaveError(undefined);
    onSavingChange(true);
    try {
      await onSave(changes, editSession.version);
      setEditSession(null);
    } catch (error) {
      // The capability adapter also reports the write globally. Keep a local
      // explanation beside the unsaved draft after its toast disappears.
      setSaveError(
        error instanceof Error
          ? error.message
          : "Không thể lưu hồ sơ. Vui lòng thử lại.",
      );
    } finally {
      onSavingChange(false);
    }
  };

  return (
    <>
      <header className="profile-header">
        <div className="profile-title">
          <UserRound className="icon" aria-hidden="true" />
          <span className="profile-title-copy">
            <span>{display(lead?.name, translate("leads.fallback_name"))}</span>
            <small>{display(lead?.phone, translate("leads.no_phone"))}</small>
          </span>
        </div>
        {showClose ? (
          <div className="context-header-actions">
            <CloseButton
              size="sm"
              className="uu-scope context-close"
              label="Đóng thông tin ứng viên"
              isDisabled={isSaving}
              onPress={closeButtonPress}
            />
          </div>
        ) : null}
      </header>

      <div className="profile-scroll">
        <section className="context-overview candidate-contact-card">
          <div className="candidate-contact-heading">
            <Phone className="icon" aria-hidden="true" />
            <h2>Liên hệ ứng viên</h2>
            <Badge
              type="pill-color"
              color={lead?.phone?.trim() ? "success" : "warning"}
              size="sm"
              className="uu-scope candidate-contact-status"
            >
              {lead?.phone?.trim() ? "Đã có số" : "Cần số điện thoại"}
            </Badge>
          </div>
          <p>
            {lead?.phone?.trim()
              ? "Đã có số điện thoại để liên hệ."
              : "Cần bổ sung số điện thoại để liên hệ."}
          </p>
          <p className="candidate-contact-hint">
            Họ tên được khuyến khích; nguyện vọng và năm sinh có thể bổ sung
            sau.
          </p>
        </section>
        <section className="context-card tt-card tt-card-sm">
          <div className="section-head">
            <h3>{translate("leads.profile_title")}</h3>
            {canEdit &&
            lead &&
            lead.version != null &&
            onSave &&
            !editSession ? (
              <Button
                type="button"
                size="xs"
                color="tertiary"
                className="uu-scope candidate-profile-edit-trigger"
                aria-label="Chỉnh sửa hồ sơ ứng viên"
                iconLeading={<Pencil className="size-3.5" aria-hidden="true" />}
                onPress={() => {
                  if (lead.version == null) return;
                  const initial = candidateProfileDraft(lead);
                  setSaveError(undefined);
                  setEditSession({
                    initial,
                    draft: { ...initial },
                    version: lead.version,
                  });
                }}
              >
                Sửa
              </Button>
            ) : null}
          </div>
          {editSession ? (
            <form
              className="uu-scope candidate-profile-edit-form grid gap-4"
              aria-label="Chỉnh sửa hồ sơ ứng viên"
              aria-busy={isSaving}
              onSubmit={(event) => {
                event.preventDefault();
                void saveProfile();
              }}
            >
              <div className="candidate-profile-fields">
                {orderedProfileFields.map((field) => {
                  const inputId = `candidate-profile-${field.key}`;
                  return (
                    <TextField
                      key={field.key}
                      id={inputId}
                      name={field.key}
                      className="min-w-0"
                      value={editSession.draft[field.key]}
                      isDisabled={isSaving}
                      onChange={(next) =>
                        setEditSession((current) =>
                          current
                            ? {
                                ...current,
                                draft: { ...current.draft, [field.key]: next },
                              }
                            : current,
                        )
                      }
                    >
                      <Label>
                        {field.key === "desired_job"
                          ? "Nguyện vọng"
                          : translate(field.labelKey)}
                      </Label>
                      <InputBase
                        wrapperClassName="candidate-profile-field-input"
                        autoFocus={field.key === "phone"}
                        inputMode={field.inputMode}
                        type={
                          field.inputMode === "numeric"
                            ? "number"
                            : field.inputMode === "tel"
                              ? "tel"
                              : "text"
                        }
                        autoComplete={
                          field.key === "name"
                            ? "name"
                            : field.key === "phone"
                              ? "tel"
                              : "off"
                        }
                        min={field.min}
                        max={field.max}
                      />
                    </TextField>
                  );
                })}
              </div>
              <TextArea
                id="candidate-profile-notes"
                label="Ghi chú (CCCD, chỗ ở, xe đưa đón và thông tin khác)"
                value={editSession.draft.notes}
                rows={5}
                isDisabled={isSaving}
                onChange={(next) =>
                  setEditSession((current) =>
                    current
                      ? { ...current, draft: { ...current.draft, notes: next } }
                      : current,
                  )
                }
              />
              {saveError ? (
                <p role="alert" className="text-helper text-error-primary">
                  {saveError}
                </p>
              ) : null}
              <div className="candidate-profile-edit-actions flex flex-wrap justify-end gap-2">
                <Button
                  type="button"
                  size="sm"
                  color="secondary"
                  className="uu-scope"
                  isDisabled={isSaving}
                  iconLeading={<X className="size-4" aria-hidden="true" />}
                  onPress={cancelEditing}
                >
                  {translate("ra.action.cancel")}
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  color="primary"
                  className="uu-scope"
                  isDisabled={isSaving}
                  iconLeading={
                    isSaving ? (
                      <LoaderCircle
                        className="size-4 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    ) : (
                      <Save className="size-4" aria-hidden="true" />
                    )
                  }
                >
                  {isSaving
                    ? translate("crm.common.saving")
                    : translate("crm.common.save_changes")}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <div className="candidate-info-grid candidate-priority-fields">
                {PRIORITY_INFO_KEYS.map((key) => {
                  const item = candidateInfoItems.find(
                    (field) => field.key === key,
                  );
                  return item ? (
                    <CandidateInfoRow key={item.key} item={item} />
                  ) : null;
                })}
              </div>
              <details className="candidate-extra-fields">
                <summary>Thông tin bổ sung</summary>
                <div className="candidate-info-grid">
                  {candidateInfoItems
                    .filter((item) => !PRIORITY_INFO_KEYS.includes(item.key))
                    .map((item) => (
                      <CandidateInfoRow key={item.key} item={item} />
                    ))}
                </div>
              </details>
            </>
          )}
        </section>
      </div>
    </>
  );
};

const CandidateInfoRow = ({ item }: { item: CandidateInfoItem }) => {
  const Icon = item.Icon;

  return (
    <div
      className={`candidate-info-row ${item.complete ? "filled" : ""}`}
      data-field={item.key}
    >
      <span className="candidate-info-icon">
        <Icon className="icon" aria-hidden="true" />
      </span>
      <div className="candidate-info-copy">
        <span className="candidate-info-label">
          {item.label}
          {item.key === "phone" ? (
            <span className="candidate-field-priority">Bắt buộc</span>
          ) : null}
          {item.key === "birth" ? (
            <span className="candidate-field-optional">Tùy chọn</span>
          ) : null}
        </span>
        {item.noteItems ? (
          <ul className="candidate-info-value candidate-note-list">
            {item.noteItems.map((note, index) => (
              <li key={`${index}-${note}`}>{note}</li>
            ))}
          </ul>
        ) : (
          <span className="candidate-info-value">{item.value}</span>
        )}
      </div>
      <span className="candidate-info-state" aria-hidden="true">
        {item.complete ? <Check className="icon" /> : null}
      </span>
    </div>
  );
};
